import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, Download, TrendingUp, DollarSign, Minus, LogOut, FileText, Loader2, ChevronLeft, ChevronRight, Calendar, BarChart3, Landmark, Banknote, CreditCard, X, Edit2, Trash2, Save, Plus, Wallet, Car, Info, HelpCircle } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subWeeks, subMonths, addWeeks, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import TwoFactorAuthModal from '@/components/TwoFactorAuthModal';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL; // Alias pour compatibilité

// Composant InfoTooltip pour expliquer les calculs
const InfoTooltip = ({ title, description, formula }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="w-5 h-5 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-slate-600 hover:text-slate-800 transition-colors"
        title="Cliquez pour plus d'infos"
      >
        <Info className="w-3 h-3" />
      </button>
      {isOpen && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl">
          <div className="font-semibold text-sm mb-1 text-blue-300">{title}</div>
          <p className="text-slate-300 mb-2">{description}</p>
          {formula && (
            <div className="bg-slate-800 rounded px-2 py-1 font-mono text-[10px] text-green-300">
              📊 {formula}
            </div>
          )}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-900"></div>
        </div>
      )}
    </div>
  );
};

// Composant PageHelp pour expliquer le fonctionnement d'une page
const PageHelp = ({ title, description, items }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-blue-800">{title}</span>
        </div>
        <Button variant="ghost" size="sm" className="text-blue-600">
          {isExpanded ? 'Masquer' : 'Comment ça marche ?'}
        </Button>
      </div>
      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-700">{description}</p>
          {items && (
            <ul className="space-y-2">
              {items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-blue-500 font-bold">•</span>
                  <span className="text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default function ReportsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const [reportType, setReportType] = useState('month'); // 'week', 'month', 'quarter', 'semester', 'year', 'custom'
  const [selectedDate, setSelectedDate] = useState(new Date()); // Date de référence pour la période
  const [customDateRange, setCustomDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [depositStats, setDepositStats] = useState(null);
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReport();
      fetchDepositStats();
    }
  }, [reportType, selectedDate, customDateRange, isAuthenticated]);

  const fetchDepositStats = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API}/api/cash-deposits/stats?period=${reportType}&reference_date=${dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepositStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats dépôts:', error);
    }
  };

  // Navigation entre les périodes
  const goToPreviousPeriod = () => {
    if (reportType === 'week') {
      setSelectedDate(subWeeks(selectedDate, 1));
    } else if (reportType === 'month') {
      setSelectedDate(subMonths(selectedDate, 1));
    } else if (reportType === 'quarter') {
      setSelectedDate(subMonths(selectedDate, 3));
    } else if (reportType === 'semester') {
      setSelectedDate(subMonths(selectedDate, 6));
    } else if (reportType === 'year') {
      setSelectedDate(new Date(selectedDate.getFullYear() - 1, 0, 1));
    }
  };

  const goToNextPeriod = () => {
    if (reportType === 'week') {
      setSelectedDate(addWeeks(selectedDate, 1));
    } else if (reportType === 'month') {
      setSelectedDate(addMonths(selectedDate, 1));
    } else if (reportType === 'quarter') {
      setSelectedDate(addMonths(selectedDate, 3));
    } else if (reportType === 'semester') {
      setSelectedDate(addMonths(selectedDate, 6));
    } else if (reportType === 'year') {
      setSelectedDate(new Date(selectedDate.getFullYear() + 1, 0, 1));
    }
  };

  const goToCurrentPeriod = () => {
    setSelectedDate(new Date());
  };

  // Obtenir le libellé de la période
  const getPeriodLabel = () => {
    if (reportType === 'week') {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return `Semaine du ${format(start, 'd MMM', { locale: fr })} au ${format(end, 'd MMM yyyy', { locale: fr })}`;
    } else if (reportType === 'month') {
      return format(selectedDate, 'MMMM yyyy', { locale: fr });
    } else if (reportType === 'quarter') {
      const quarter = Math.ceil((selectedDate.getMonth() + 1) / 3);
      return `T${quarter} ${format(selectedDate, 'yyyy')} (${['Jan-Mar', 'Avr-Jun', 'Jul-Sep', 'Oct-Déc'][quarter - 1]})`;
    } else if (reportType === 'semester') {
      const semester = selectedDate.getMonth() < 6 ? 1 : 2;
      return `S${semester} ${format(selectedDate, 'yyyy')} (${semester === 1 ? 'Jan-Juin' : 'Juil-Déc'})`;
    } else if (reportType === 'custom') {
      return `Du ${customDateRange.start} au ${customDateRange.end}`;
    } else {
      return `Année ${format(selectedDate, 'yyyy')}`;
    }
  };

  const checkAuthentication = () => {
    // Vérifier si l'utilisateur vient d'une sous-page des rapports (navigation interne)
    const previousPath = sessionStorage.getItem('reports_previous_path');
    const isFromSubPage = previousPath && (
      previousPath.includes('/cash-deposits') ||
      previousPath.includes('/monthly-charges') ||
      previousPath.includes('/financial-summary') ||
      previousPath.includes('/accounting') ||
      previousPath.includes('/cash-flow')
    );
    
    // Vérifier si déjà authentifié dans cette session
    const alreadyAuthenticated = sessionStorage.getItem('reports_authenticated') === 'true';
    
    if (alreadyAuthenticated && isFromSubPage) {
      // L'utilisateur revient d'une sous-page et était déjà authentifié
      setIsAuthenticated(true);
      setShowAuthModal(false);
      // Nettoyer le chemin précédent
      sessionStorage.removeItem('reports_previous_path');
    } else if (alreadyAuthenticated) {
      // Authentifié mais pas depuis une sous-page - vérifier si c'est récent (5 min)
      const authTime = sessionStorage.getItem('reports_auth_time');
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (authTime && (now - parseInt(authTime)) < fiveMinutes) {
        setIsAuthenticated(true);
        setShowAuthModal(false);
      } else {
        // Session expirée, demander ré-authentification
        sessionStorage.removeItem('reports_authenticated');
        sessionStorage.removeItem('reports_auth_time');
        setShowAuthModal(true);
      }
    } else {
      // Pas authentifié, afficher le modal
      setShowAuthModal(true);
    }
  };

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    setShowAuthModal(false);
    sessionStorage.setItem('reports_authenticated', 'true');
    sessionStorage.setItem('reports_auth_time', Date.now().toString());
    toast.success('Double authentification réussie !');
  };

  const exportToPdf = async () => {
    if (!reportData) return;
    
    setExportingPdf(true);
    toast.info('Génération du PDF professionnel en cours...');
    
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      
      // Couleurs
      const primaryColor = [79, 70, 229]; // Indigo
      const darkColor = [15, 23, 42];
      const grayColor = [100, 116, 139];
      const greenColor = [16, 185, 129];
      const redColor = [239, 68, 68];
      const lightGray = [241, 245, 249];
      
      // ========== EN-TÊTE ==========
      // Bandeau coloré
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      // Logo / Nom entreprise
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('MPHONE', margin, 18);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Gestion de Caisse', margin, 25);
      
      // Période (côté droit)
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      const periodLabel = reportType === 'week' ? 'RAPPORT HEBDOMADAIRE' : 
                          reportType === 'month' ? 'RAPPORT MENSUEL' : 'RAPPORT ANNUEL';
      pdf.text(periodLabel, pageWidth - margin, 15, { align: 'right' });
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Du ${reportData.startDate} au ${reportData.endDate}`, pageWidth - margin, 22, { align: 'right' });
      pdf.text(`Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}`, pageWidth - margin, 29, { align: 'right' });
      
      yPos = 45;
      
      // ========== RÉSUMÉ FINANCIER ==========
      pdf.setFillColor(...lightGray);
      pdf.roundedRect(margin, yPos, contentWidth, 45, 3, 3, 'F');
      
      yPos += 8;
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RÉSUMÉ FINANCIER', margin + 5, yPos);
      
      yPos += 10;
      const boxWidth = (contentWidth - 20) / 4;
      
      // Box 1: Total Entrées
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin + 5, yPos, boxWidth, 22, 2, 2, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(...grayColor);
      pdf.text('TOTAL ENTRÉES', margin + 7, yPos + 6);
      pdf.setFontSize(14);
      pdf.setTextColor(...greenColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${formatCurrency(reportData.summary.totalRevenue)}`, margin + 7, yPos + 16);
      
      // Box 2: Total Sorties
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin + 10 + boxWidth, yPos, boxWidth, 22, 2, 2, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(...grayColor);
      pdf.setFont('helvetica', 'normal');
      pdf.text('TOTAL SORTIES', margin + 12 + boxWidth, yPos + 6);
      pdf.setFontSize(14);
      pdf.setTextColor(...redColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`-${formatCurrency(reportData.summary.totalExpenses)}`, margin + 12 + boxWidth, yPos + 16);
      
      // Box 3: Solde Net
      const soldeNet = reportData.summary.totalRevenue - reportData.summary.totalExpenses;
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin + 15 + boxWidth * 2, yPos, boxWidth, 22, 2, 2, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(...grayColor);
      pdf.setFont('helvetica', 'normal');
      pdf.text('SOLDE NET', margin + 17 + boxWidth * 2, yPos + 6);
      pdf.setFontSize(14);
      pdf.setTextColor(...(soldeNet >= 0 ? greenColor : redColor));
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${formatCurrency(soldeNet)}`, margin + 17 + boxWidth * 2, yPos + 16);
      
      // Box 4: Nb Transactions
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin + 20 + boxWidth * 3, yPos, boxWidth - 5, 22, 2, 2, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(...grayColor);
      pdf.setFont('helvetica', 'normal');
      pdf.text('TRANSACTIONS', margin + 22 + boxWidth * 3, yPos + 6);
      pdf.setFontSize(14);
      pdf.setTextColor(...primaryColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${reportData.entries?.length || 0}`, margin + 22 + boxWidth * 3, yPos + 16);
      
      yPos += 35;
      
      // ========== TABLEAU DES ENTRÉES ==========
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DÉTAIL DES ENTRÉES', margin, yPos);
      yPos += 6;
      
      // En-tête tableau
      pdf.setFillColor(...primaryColor);
      pdf.rect(margin, yPos, contentWidth, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      
      const colWidths = [22, 55, 25, 25, 28, 25];
      let xPos = margin + 2;
      const headers = ['Date', 'Description', 'Qté', 'P.U.', 'Total', 'Paiement'];
      headers.forEach((header, i) => {
        pdf.text(header, xPos, yPos + 5.5);
        xPos += colWidths[i];
      });
      
      yPos += 8;
      
      // Lignes du tableau
      const entries = reportData.entries || [];
      const maxRows = Math.min(entries.length, 20); // Limiter à 20 lignes
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      
      for (let i = 0; i < maxRows; i++) {
        const entry = entries[i];
        const rowY = yPos + (i * 6);
        
        // Alternance de couleur
        if (i % 2 === 0) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, rowY, contentWidth, 6, 'F');
        }
        
        pdf.setTextColor(...darkColor);
        xPos = margin + 2;
        
        // Date
        pdf.text(entry.date || '', xPos, rowY + 4);
        xPos += colWidths[0];
        
        // Description (tronquée si trop longue)
        const desc = (entry.description || '').substring(0, 35);
        pdf.text(desc, xPos, rowY + 4);
        xPos += colWidths[1];
        
        // Quantité
        pdf.text(`${entry.quantity || 1}`, xPos, rowY + 4);
        xPos += colWidths[2];
        
        // Prix unitaire
        pdf.text(`${(entry.sale_price || 0).toFixed(2)}€`, xPos, rowY + 4);
        xPos += colWidths[3];
        
        // Total
        const total = (entry.sale_price || 0) * (entry.quantity || 1);
        pdf.setTextColor(...greenColor);
        pdf.text(`${total.toFixed(2)}€`, xPos, rowY + 4);
        xPos += colWidths[4];
        
        // Paiement
        pdf.setTextColor(...darkColor);
        const payment = entry.payment_method === 'cash' ? 'Espèces' : 
                       entry.payment_method === 'card' ? 'Carte' : 
                       entry.payment_method === 'mixte' ? 'Mixte' : entry.payment_method || '';
        pdf.text(payment, xPos, rowY + 4);
      }
      
      if (entries.length > maxRows) {
        yPos += maxRows * 6 + 4;
        pdf.setTextColor(...grayColor);
        pdf.setFontSize(8);
        pdf.text(`... et ${entries.length - maxRows} autres entrées`, margin, yPos);
      }
      
      yPos += maxRows * 6 + 10;
      
      // ========== TABLEAU DES SORTIES ==========
      if (expenses.length > 0) {
        pdf.setTextColor(...darkColor);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DÉTAIL DES SORTIES', margin, yPos);
        yPos += 6;
        
        // En-tête tableau sorties
        pdf.setFillColor(...redColor);
        pdf.rect(margin, yPos, contentWidth, 8, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        
        xPos = margin + 2;
        const expenseHeaders = ['Date', 'Description', 'Montant', 'Paiement'];
        const expenseColWidths = [25, 90, 35, 30];
        expenseHeaders.forEach((header, i) => {
          pdf.text(header, xPos, yPos + 5.5);
          xPos += expenseColWidths[i];
        });
        
        yPos += 8;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        
        const maxExpenseRows = Math.min(expenses.length, 10);
        
        for (let i = 0; i < maxExpenseRows; i++) {
          const expense = expenses[i];
          const rowY = yPos + (i * 6);
          
          if (i % 2 === 0) {
            pdf.setFillColor(254, 242, 242);
            pdf.rect(margin, rowY, contentWidth, 6, 'F');
          }
          
          pdf.setTextColor(...darkColor);
          xPos = margin + 2;
          
          pdf.text(expense.date || '', xPos, rowY + 4);
          xPos += expenseColWidths[0];
          
          pdf.text((expense.description || '').substring(0, 55), xPos, rowY + 4);
          xPos += expenseColWidths[1];
          
          pdf.setTextColor(...redColor);
          pdf.text(`-${(expense.amount || 0).toFixed(2)}€`, xPos, rowY + 4);
          xPos += expenseColWidths[2];
          
          pdf.setTextColor(...darkColor);
          const expPayment = expense.payment_method === 'cash' ? 'Espèces' : 
                            expense.payment_method === 'card' ? 'Carte' : expense.payment_method || '';
          pdf.text(expPayment, xPos, rowY + 4);
        }
        
        yPos += maxExpenseRows * 6 + 10;
      }
      
      // ========== RÉCAPITULATIF PAR MODE DE PAIEMENT ==========
      if (yPos < pageHeight - 60) {
        pdf.setFillColor(...lightGray);
        pdf.roundedRect(margin, yPos, contentWidth, 35, 3, 3, 'F');
        
        yPos += 8;
        pdf.setTextColor(...darkColor);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('RÉCAPITULATIF PAR MODE DE PAIEMENT', margin + 5, yPos);
        
        yPos += 8;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        
        const cashTotal = entries.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + (e.sale_price || 0) * (e.quantity || 1), 0);
        const cardTotal = entries.filter(e => e.payment_method === 'card').reduce((sum, e) => sum + (e.sale_price || 0) * (e.quantity || 1), 0);
        const mixteTotal = entries.filter(e => e.payment_method === 'mixte').reduce((sum, e) => sum + (e.sale_price || 0) * (e.quantity || 1), 0);
        
        pdf.text(`Espèces: ${formatCurrency(cashTotal)}`, margin + 10, yPos);
        pdf.text(`Carte: ${formatCurrency(cardTotal)}`, margin + 70, yPos);
        pdf.text(`Mixte: ${formatCurrency(mixteTotal)}`, margin + 130, yPos);
        
        // Dépôts banque si disponible
        if (depositStats) {
          yPos += 8;
          pdf.setTextColor(...primaryColor);
          pdf.text(`Déposé en banque: ${formatCurrency(depositStats.total_deposited)}`, margin + 10, yPos);
          pdf.setTextColor(depositStats.cash_remaining >= 0 ? greenColor[0] : redColor[0], 
                          depositStats.cash_remaining >= 0 ? greenColor[1] : redColor[1],
                          depositStats.cash_remaining >= 0 ? greenColor[2] : redColor[2]);
          pdf.text(`Espèces restantes: ${formatCurrency(depositStats.cash_remaining)}`, margin + 70, yPos);
        }
      }
      
      // ========== PIED DE PAGE ==========
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('MPHONE - Gestion de Caisse', margin, pageHeight - 7);
      pdf.text(`Page 1/1 - Document généré automatiquement`, pageWidth - margin, pageHeight - 7, { align: 'right' });
      
      // Télécharger
      const fileName = `MPHONE_Rapport_${reportType}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      pdf.save(fileName);
      
      toast.success('PDF professionnel téléchargé !');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let startDate, endDate;

      if (reportType === 'week') {
        startDate = format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        endDate = format(endOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      } else if (reportType === 'month') {
        startDate = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
        endDate = format(endOfMonth(selectedDate), 'yyyy-MM-dd');
      } else {
        startDate = format(startOfYear(selectedDate), 'yyyy-MM-dd');
        endDate = format(endOfYear(selectedDate), 'yyyy-MM-dd');
      }

      // Fetch ALL entries and expenses (without user_id filter for historical data)
      const [entriesResponse, expensesResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/entries/all-for-reports`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/expenses/all-for-reports`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const allEntries = entriesResponse.data;
      const allExpenses = expensesResponse.data;
      
      // Filter by date range
      const filteredEntries = allEntries.filter(e => e.date >= startDate && e.date <= endDate);
      const filteredExpenses = allExpenses.filter(e => e.date >= startDate && e.date <= endDate);
      
      // Stocker les expenses pour le modal
      setExpenses(filteredExpenses);

      // Calculate entry statistics
      const totalRevenue = filteredEntries.reduce((sum, e) => sum + (e.sale_price * e.quantity), 0);
      const totalCash = filteredEntries.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + (e.sale_price * e.quantity), 0);
      const totalCard = filteredEntries.filter(e => e.payment_method === 'card').reduce((sum, e) => sum + (e.sale_price * e.quantity), 0);
      const totalMargin = filteredEntries.reduce((sum, e) => sum + e.margin, 0);

      // Calculate expense statistics
      const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
      const expensesCash = filteredExpenses.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + e.amount, 0);
      const expensesCard = filteredExpenses.filter(e => e.payment_method === 'card').reduce((sum, e) => sum + e.amount, 0);

      // Net amounts
      const netRevenue = totalRevenue - totalExpenses;
      const netCash = totalCash - expensesCash;
      const netCard = totalCard - expensesCard;

      // Group by date for chart
      const byDate = {};
      
      filteredEntries.forEach(e => {
        if (!byDate[e.date]) {
          byDate[e.date] = { date: e.date, revenue: 0, cash: 0, card: 0, margin: 0, expenses: 0, count: 0 };
        }
        byDate[e.date].revenue += e.sale_price * e.quantity;
        byDate[e.date].cash += e.payment_method === 'cash' ? e.sale_price * e.quantity : 0;
        byDate[e.date].card += e.payment_method === 'card' ? e.sale_price * e.quantity : 0;
        byDate[e.date].margin += e.margin;
        byDate[e.date].count += 1;
      });

      // Add expenses to chart data
      filteredExpenses.forEach(e => {
        if (!byDate[e.date]) {
          byDate[e.date] = { date: e.date, revenue: 0, cash: 0, card: 0, margin: 0, expenses: 0, count: 0 };
        }
        byDate[e.date].expenses += e.amount;
      });

      const chartData = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

      setReportData({
        summary: {
          totalRevenue: netRevenue,
          totalCash: netCash,
          totalCard: netCard,
          totalMargin,
          totalExpenses,
          transactionCount: filteredEntries.length,
          averageTransaction: filteredEntries.length > 0 ? totalRevenue / filteredEntries.length : 0
        },
        chartData,
        startDate,
        endDate
      });
    } catch (error) {
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const getReportTitle = () => {
    if (reportType === 'week') return 'Rapport Hebdomadaire';
    if (reportType === 'month') return 'Rapport Mensuel';
    if (reportType === 'quarter') return 'Rapport Trimestriel';
    if (reportType === 'semester') return 'Rapport Semestriel';
    if (reportType === 'custom') return 'Rapport Personnalisé';
    return 'Rapport Annuel';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reports-page">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200 h-16 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Rapports</h1>
          <span className="text-sm text-slate-500">|</span>
          <span className="text-sm text-slate-600">Bonjour, {user?.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Navigation interne section Rapports */}
          <Button
            onClick={() => navigate('/cash-deposits')}
            data-testid="cash-deposits-button"
            variant="outline"
            size="sm"
            className="border-emerald-300 hover:bg-emerald-50 text-emerald-600"
          >
            <Landmark className="w-4 h-4 mr-2" />
            Dépôts
          </Button>
          <Button
            onClick={() => navigate('/monthly-charges')}
            data-testid="monthly-charges-button"
            variant="outline"
            size="sm"
            className="border-orange-300 hover:bg-orange-50 text-orange-600"
          >
            <FileText className="w-4 h-4 mr-2" />
            Charges
          </Button>
          <Button
            onClick={() => navigate('/financial-summary')}
            data-testid="financial-summary-button"
            variant="outline"
            size="sm"
            className="border-purple-300 hover:bg-purple-50 text-purple-600"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Résumé
          </Button>
          <Button
            onClick={() => navigate('/accounting')}
            data-testid="accounting-button"
            variant="outline"
            size="sm"
            className="border-indigo-300 hover:bg-indigo-50 text-indigo-600"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Compta
          </Button>
          <Button
            onClick={() => navigate('/cash-flow')}
            data-testid="cash-flow-button"
            variant="outline"
            size="sm"
            className="border-teal-300 hover:bg-teal-50 text-teal-600"
          >
            <Wallet className="w-4 h-4 mr-2" />
            Espèces
          </Button>
          <Button
            onClick={() => navigate('/frais-pro')}
            data-testid="frais-pro-button"
            variant="outline"
            size="sm"
            className="border-green-300 hover:bg-green-50 text-green-600"
          >
            <Car className="w-4 h-4 mr-2" />
            Frais Pro
          </Button>
          
          {/* Séparateur */}
          <div className="h-6 w-px bg-slate-300 mx-2"></div>
          
          {/* Bouton Quitter pour sortir de la section Rapports */}
          <Button
            onClick={() => {
              // Effacer l'authentification rapports pour demander re-auth la prochaine fois
              sessionStorage.removeItem('reports_authenticated');
              navigate('/');
            }}
            data-testid="exit-reports-button"
            variant="outline"
            size="sm"
            className="border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-700"
          >
            <Home className="w-4 h-4 mr-2" />
            Quitter Rapports
          </Button>
          
          {/* Déconnexion complète */}
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="border-red-300 hover:bg-red-50 text-red-600"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      {!isAuthenticated ? (
        <main className="max-w-7xl mx-auto p-4 md:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Authentification requise...</p>
          </div>
        </main>
      ) : (
      <main className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h2 className="text-2xl font-semibold text-slate-800">{getReportTitle()}</h2>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setReportType('week')}
              variant={reportType === 'week' ? 'default' : 'outline'}
              size="sm"
              data-testid="week-button"
            >
              Semaine
            </Button>
            <Button
              onClick={() => setReportType('month')}
              variant={reportType === 'month' ? 'default' : 'outline'}
              size="sm"
              data-testid="month-button"
            >
              Mois
            </Button>
            <Button
              onClick={() => setReportType('quarter')}
              variant={reportType === 'quarter' ? 'default' : 'outline'}
              size="sm"
              data-testid="quarter-button"
            >
              Trimestre
            </Button>
            <Button
              onClick={() => setReportType('semester')}
              variant={reportType === 'semester' ? 'default' : 'outline'}
              size="sm"
              data-testid="semester-button"
            >
              Semestre
            </Button>
            <Button
              onClick={() => setReportType('year')}
              variant={reportType === 'year' ? 'default' : 'outline'}
              size="sm"
              data-testid="year-button"
            >
              Année
            </Button>
            <Button
              onClick={() => setReportType('custom')}
              variant={reportType === 'custom' ? 'default' : 'outline'}
              size="sm"
              data-testid="custom-button"
            >
              Personnalisé
            </Button>
            <Button
              onClick={exportToPdf}
              disabled={exportingPdf || !reportData}
              variant="outline"
              size="sm"
              data-testid="export-pdf-button"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              {exportingPdf ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Export PDF
            </Button>
          </div>
        </div>

        {/* Sélecteur de dates personnalisées */}
        {reportType === 'custom' && (
          <div className="flex items-center justify-center gap-4 py-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Du:</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange({...customDateRange, start: e.target.value})}
                className="px-3 py-1 border border-slate-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Au:</label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({...customDateRange, end: e.target.value})}
                className="px-3 py-1 border border-slate-300 rounded-lg text-sm"
              />
            </div>
          </div>
        )}

        {/* Navigation entre les périodes */}
        {reportType !== 'custom' && (
        <div className="flex items-center justify-center gap-4 py-4 bg-white rounded-xl border border-slate-200 shadow-sm">
          <Button
            onClick={goToPreviousPeriod}
            variant="outline"
            size="sm"
            className="border-slate-300"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Précédent
          </Button>
          
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg min-w-[300px] justify-center">
            <Calendar className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-slate-800">{getPeriodLabel()}</span>
          </div>
          
          <Button
            onClick={goToNextPeriod}
            variant="outline"
            size="sm"
            className="border-slate-300"
          >
            Suivant
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          
          <Button
            onClick={goToCurrentPeriod}
            variant="outline"
            size="sm"
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            Aujourd'hui
          </Button>
        </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Chargement...</p>
          </div>
        ) : reportData ? (
          <div ref={reportRef}>
            {/* Guide d'utilisation de la page */}
            <PageHelp
              title="📊 Page Rapports - Vue d'ensemble"
              description="Cette page affiche un résumé de votre activité sur la période sélectionnée. Tous les montants sont calculés automatiquement à partir de vos encaissements et dépenses."
              items={[
                "Utilisez les boutons Semaine/Mois/Trimestre/Année pour changer la période",
                "Les flèches permettent de naviguer entre les périodes",
                "Cliquez sur 'Sorties' pour voir le détail des dépenses",
                "Exportez en PDF pour archiver ou imprimer vos rapports"
              ]}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Total Net</span>
                  <div className="flex items-center gap-2">
                    <InfoTooltip 
                      title="Total Net (Chiffre d'affaires)"
                      description="Somme de tous les encaissements sur la période, tous modes de paiement confondus."
                      formula="Espèces + Carte + Mixte + Autres"
                    />
                    <TrendingUp className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
                <div className="text-3xl font-bold font-mono text-slate-900">
                  {formatCurrency(reportData.summary.totalRevenue)}
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl border border-emerald-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-emerald-600 uppercase">Espèces</span>
                  <div className="flex items-center gap-2">
                    <InfoTooltip 
                      title="Total Espèces"
                      description="Somme des paiements reçus en espèces sur la période."
                      formula="Σ Ventes payées en espèces"
                    />
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold font-mono text-emerald-700">
                  {formatCurrency(reportData.summary.totalCash)}
                </div>
              </div>

              <div className="bg-sky-50 rounded-xl border border-sky-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-sky-600 uppercase">Carte Bancaire</span>
                  <div className="flex items-center gap-2">
                    <InfoTooltip 
                      title="Total Carte Bancaire"
                      description="Somme des paiements reçus par carte bancaire (CB, Visa, Mastercard) sur la période."
                      formula="Σ Ventes payées par carte"
                    />
                    <DollarSign className="w-5 h-5 text-sky-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold font-mono text-sky-700">
                  {formatCurrency(reportData.summary.totalCard)}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Marge Totale</span>
                  <div className="flex items-center gap-2">
                    <InfoTooltip 
                      title="Marge Totale (Bénéfice brut)"
                      description="Différence entre le prix de vente et le prix d'achat de vos articles vendus."
                      formula="Σ (Prix vente - Prix achat) × Quantité"
                    />
                    <TrendingUp className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
                <div className="text-3xl font-bold font-mono text-slate-900">
                  {formatCurrency(reportData.summary.totalMargin)}
                </div>
              </div>

              {reportData.summary.totalExpenses > 0 && (
                <div 
                  className="bg-red-50 rounded-xl border border-red-200 shadow-sm p-6 cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => setShowExpensesModal(true)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-red-600 uppercase">Sorties</span>
                    <div className="flex items-center gap-2">
                      <InfoTooltip 
                        title="Sorties (Dépenses caisse)"
                        description="Total des dépenses effectuées depuis la caisse (achats fournisseurs, frais divers). Cliquez pour voir le détail."
                        formula="Σ Dépenses de la période"
                      />
                      <Minus className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold font-mono text-red-700">
                    -{formatCurrency(reportData.summary.totalExpenses)}
                  </div>
                  <div className="text-xs text-red-500 mt-1">Cliquez pour voir le détail</div>
                </div>
              )}
            </div>

            {/* Section Dépôts Espèces */}
            {depositStats && (
              <div className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-emerald-800 flex items-center gap-2">
                    <Landmark className="w-5 h-5" />
                    Suivi Dépôts Banque
                    <InfoTooltip 
                      title="Suivi des Dépôts en Banque"
                      description="Cette section vous permet de suivre les espèces encaissées et les dépôts effectués en banque. L'objectif est de vérifier que toutes les espèces sont bien déposées."
                      formula="Espèces restantes = Espèces encaissées - Dépôts banque - Sorties"
                    />
                  </h3>
                  <Button 
                    onClick={() => navigate('/cash-deposits')} 
                    size="sm" 
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  >
                    Gérer les dépôts
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/70 rounded-lg p-4">
                    <div className="flex items-center justify-between text-emerald-600 text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4" />
                        Espèces encaissées
                      </div>
                      <InfoTooltip 
                        title="Espèces encaissées"
                        description="Total des paiements reçus en espèces sur la période sélectionnée."
                        formula="Σ Ventes payées en espèces"
                      />
                    </div>
                    <div className="text-2xl font-bold text-slate-800">{formatCurrency(depositStats.total_cash)}</div>
                  </div>
                  
                  <div className="bg-white/70 rounded-lg p-4">
                    <div className="flex items-center justify-between text-blue-600 text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Carte encaissée
                      </div>
                      <InfoTooltip 
                        title="Carte encaissée"
                        description="Total des paiements reçus par carte bancaire. Ce montant est automatiquement viré sur votre compte bancaire."
                        formula="Σ Ventes payées par carte"
                      />
                    </div>
                    <div className="text-2xl font-bold text-slate-800">{formatCurrency(depositStats.total_card)}</div>
                  </div>
                  
                  <div className="bg-white/70 rounded-lg p-4">
                    <div className="flex items-center justify-between text-teal-600 text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <Landmark className="w-4 h-4" />
                        Déposé en banque
                      </div>
                      <InfoTooltip 
                        title="Déposé en banque"
                        description="Total des espèces que vous avez déposées à la banque sur la période."
                        formula="Σ Bordereaux de dépôt"
                      />
                    </div>
                    <div className="text-2xl font-bold text-slate-800">{formatCurrency(depositStats.total_deposited)}</div>
                    <div className="text-xs text-slate-500">{depositStats.deposit_count} dépôt(s)</div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${depositStats.cash_remaining >= 0 ? 'bg-amber-100/70' : 'bg-red-100/70'}`}>
                    <div className={`flex items-center justify-between text-sm mb-1 ${depositStats.cash_remaining >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4" />
                        Espèces restantes
                      </div>
                      <InfoTooltip 
                        title="Espèces restantes en caisse"
                        description={depositStats.cash_remaining >= 0 
                          ? "Montant d'espèces qui devrait être dans votre caisse. À déposer en banque régulièrement."
                          : "⚠️ Montant négatif = écart ! Il manque des espèces par rapport aux encaissements enregistrés."}
                        formula="Espèces encaissées - Dépôts banque - Sorties espèces"
                      />
                    </div>
                    <div className={`text-2xl font-bold ${depositStats.cash_remaining >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                      {formatCurrency(depositStats.cash_remaining)}
                    </div>
                    <div className={`text-xs ${depositStats.cash_remaining >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                      {depositStats.cash_remaining >= 0 ? 'À déposer' : 'Écart!'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-slate-800">Transactions</h3>
                  <InfoTooltip 
                    title="Nombre de transactions"
                    description="Nombre total de ventes/encaissements enregistrés sur la période."
                    formula="Compte des entrées de caisse"
                  />
                </div>
                <p className="text-4xl font-bold text-slate-900">{reportData.summary.transactionCount}</p>
                <p className="text-sm text-slate-500 mt-2">
                  Moyenne: {formatCurrency(reportData.summary.averageTransaction)}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Période</h3>
                <p className="text-sm text-slate-600">
                  Du {reportData.startDate} au {reportData.endDate}
                </p>
              </div>
            </div>

            {reportData.chartData.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Évolution des encaissements</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={reportData.chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line type="monotone" dataKey="revenue" stroke="#0F172A" strokeWidth={2} name="Total" />
                    <Line type="monotone" dataKey="cash" stroke="#10B981" strokeWidth={2} name="Espèces" />
                    <Line type="monotone" dataKey="card" stroke="#0EA5E9" strokeWidth={2} name="CB" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-500">Aucune donnée disponible</p>
          </div>
        )}
      </main>
      )}

      <TwoFactorAuthModal
        isOpen={showAuthModal}
        onClose={() => {
          if (!isAuthenticated) {
            navigate('/');
          } else {
            setShowAuthModal(false);
          }
        }}
        onAuthenticated={handleAuthenticated}
        purpose="reports"
        title="Accès aux Rapports - Authentification SMS"
      />

      {/* Modal Historique des Sorties */}
      {showExpensesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Minus className="w-5 h-5 text-red-500" />
                Historique des Sorties
              </h2>
              <Button onClick={() => setShowExpensesModal(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {expenses.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Aucune sortie pour cette période
                </div>
              ) : (
                <div className="space-y-3">
                  {expenses.map((expense) => (
                    <div 
                      key={expense.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">{expense.description}</div>
                        <div className="text-sm text-slate-500">
                          {expense.date} • {expense.payment_method === 'cash' ? 'Espèces' : expense.payment_method === 'card' ? 'Carte' : expense.payment_method}
                        </div>
                        {expense.category && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded">
                            {expense.category}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-lg font-bold text-red-600">
                          -{formatCurrency(expense.amount)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-blue-600 hover:bg-blue-50"
                            onClick={() => {
                              setEditingExpense(expense);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600 hover:bg-red-50"
                            onClick={async () => {
                              if (!window.confirm('Supprimer cette sortie ?')) return;
                              try {
                                await axios.delete(`${API}/api/expenses/${expense.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('Sortie supprimée');
                                setExpenses(expenses.filter(e => e.id !== expense.id));
                                fetchReport();
                              } catch (error) {
                                toast.error('Erreur lors de la suppression');
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Total */}
              <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                <span className="font-medium text-slate-600">Total des sorties</span>
                <span className="text-xl font-bold text-red-600">
                  -{formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0))}
                </span>
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <Button onClick={() => setShowExpensesModal(false)} className="w-full">
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modification Sortie */}
      {editingExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">Modifier la sortie</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
                <input
                  type="text"
                  value={editingExpense.description}
                  onChange={(e) => setEditingExpense({...editingExpense, description: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Montant</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingExpense.amount}
                    onChange={(e) => setEditingExpense({...editingExpense, amount: parseFloat(e.target.value) || 0})}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Mode paiement</label>
                  <select
                    value={editingExpense.payment_method}
                    onChange={(e) => setEditingExpense({...editingExpense, payment_method: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  >
                    <option value="cash">Espèces</option>
                    <option value="card">Carte</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Date</label>
                <input
                  type="date"
                  value={editingExpense.date}
                  onChange={(e) => setEditingExpense({...editingExpense, date: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-200 flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setEditingExpense(null)}
              >
                Annuler
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={async () => {
                  try {
                    await axios.put(`${API}/api/expenses/${editingExpense.id}`, {
                      description: editingExpense.description,
                      amount: editingExpense.amount,
                      payment_method: editingExpense.payment_method,
                      date: editingExpense.date
                    }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Sortie modifiée');
                    setEditingExpense(null);
                    fetchReport();
                  } catch (error) {
                    toast.error('Erreur lors de la modification');
                  }
                }}
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
